import React, { useEffect, useState } from "react";
import { authFetch, authFetchForm, fetchMenu } from "../../api";
import { FiTrash2, FiPlus } from "react-icons/fi";
import IconPicker, { iconsToList } from "../IconPicker";

const inputClass = "w-full px-3 py-1.5 rounded-md bg-white/5 border border-navy-border text-white text-sm focus:outline-none focus:border-accent-light";
const emptyItem = { title: "", subtitle: "", icons: "", order: 0, is_active: true };

// AI Tools' 6 categories (AI Document Tools, AI Image Tools, ...) use the same ServiceCard model
// as the main Services - this mirrors ServicesSettings.js, just sourcing its title dropdown from
// the "AI Tools" menu branch instead of "Services", and dropping the rich-text description (this
// hub shows categories as cards only - no per-category detail content). Its individual tools (the
// level below each category) will get their own "Sub Services"-equivalent form later.
export default function AIToolsCategoriesSettings() {
  const [items, setItems] = useState([]);
  const [imageFiles, setImageFiles] = useState({});
  const [error, setError] = useState("");
  const [newItem, setNewItem] = useState(emptyItem);
  const [newImageFile, setNewImageFile] = useState(null);
  const [addFormKey, setAddFormKey] = useState(0);
  const [titleOptions, setTitleOptions] = useState([]);

  const load = () => {
    authFetch("/core/service-cards/")
      .then((res) => res.json())
      .then((data) => setItems(data.sort((a, b) => a.order - b.order)))
      .catch(() => setError("Failed to load service cards."));
  };

  useEffect(load, []);
  useEffect(() => {
    fetchMenu()
      .then((menuItems) => {
        const aiToolsMenu = menuItems.find((m) => m.parent === null && m.title === "AI Tools");
        const categories = aiToolsMenu
          ? menuItems.filter((m) => m.parent === aiToolsMenu.id).sort((a, b) => a.order - b.order)
          : [];
        setTitleOptions(categories.map((m) => m.title));
      })
      .catch(() => {});
  }, []);

  const updateField = (id, field, value) => {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, [field]: value } : it)));
  };

  const toggleIcon = (id, key) => {
    setItems((prev) => prev.map((it) => {
      if (it.id !== id) return it;
      const current = iconsToList(it.icons);
      const next = current.includes(key) ? current.filter((k) => k !== key) : [...current, key];
      return { ...it, icons: next.join(",") };
    }));
  };

  const toggleNewIcon = (key) => {
    setNewItem((prev) => {
      const current = iconsToList(prev.icons);
      const next = current.includes(key) ? current.filter((k) => k !== key) : [...current, key];
      return { ...prev, icons: next.join(",") };
    });
  };

  const buildFormData = (item) => {
    const fd = new FormData();
    fd.append("title", item.title);
    fd.append("subtitle", item.subtitle);
    fd.append("icons", item.icons);
    fd.append("order", item.order);
    fd.append("is_active", item.is_active);
    return fd;
  };

  const saveItem = async (item) => {
    setError("");
    const fd = buildFormData(item);
    if (imageFiles[item.id]) fd.append("image", imageFiles[item.id]);
    const res = await authFetchForm(`/core/service-cards/${item.id}/`, fd);
    if (!res.ok) {
      setError("Failed to save service card.");
      return;
    }
    setImageFiles((prev) => { const next = { ...prev }; delete next[item.id]; return next; });
    load();
  };

  const deleteItem = async (id) => {
    if (!window.confirm("Delete this service card?")) return;
    await authFetch(`/core/service-cards/${id}/`, { method: "DELETE" });
    load();
  };

  const addItem = async (e) => {
    e.preventDefault();
    if (!newItem.title) return;
    setError("");
    const fd = buildFormData({ ...newItem, order: items.length + 1 });
    if (newImageFile) fd.append("image", newImageFile);
    const res = await authFetchForm("/core/service-cards/", fd, "POST");
    if (!res.ok) {
      setError("Failed to add service card.");
      return;
    }
    setNewItem(emptyItem);
    setNewImageFile(null);
    setAddFormKey((k) => k + 1);
    load();
  };

  // Only show AI Tools categories here - the main Services list keeps its own separate view
  // even though both live in the same ServiceCard table.
  const aiItems = items.filter((it) => titleOptions.includes(it.title));

  return (
    <div className="w-full">
      <h1 className="text-2xl font-bold mb-2">AI Tools</h1>
      <p className="text-sm text-gray-400 mb-6">
        Manage the AI Tools categories (AI Document Tools, AI Image Tools, ...): image, title, subtitle, and the
        tool icons shown on each category's card. Each category's individual tools (e.g. AI Document
        Summarization) get their own content later.
      </p>

      {error && <p className="text-red-400 mb-4">{error}</p>}

      <form key={addFormKey} onSubmit={addItem} className="mb-6 bg-navy border border-navy-border rounded-xl p-6">
        <h2 className="text-lg font-semibold mb-4">Add New AI Tools Category</h2>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs text-gray-400 mb-1">Title</label>
            <select
              className={inputClass}
              style={{ colorScheme: "dark" }}
              value={newItem.title}
              onChange={(e) => setNewItem({ ...newItem, title: e.target.value })}
            >
              <option value="" style={{ backgroundColor: "#13202D", color: "#ffffff" }}>Select a category...</option>
              {titleOptions.map((title) => (
                <option key={title} value={title} style={{ backgroundColor: "#13202D", color: "#ffffff" }}>{title}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Subtitle</label>
            <input
              className={inputClass}
              value={newItem.subtitle}
              onChange={(e) => setNewItem({ ...newItem, subtitle: e.target.value })}
              placeholder="e.g. Summarize, translate, and generate documents with AI"
            />
          </div>
        </div>

        <div className="mt-4">
          <label className="block text-xs text-gray-400 mb-1">Tools Used</label>
          <IconPicker selected={iconsToList(newItem.icons)} onToggle={toggleNewIcon} />
        </div>

        <div className="flex items-center gap-6 mt-4">
          <div>
            <label className="block text-xs text-gray-400 mb-1">Image</label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setNewImageFile(e.target.files[0])}
              className="text-xs text-gray-400"
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-300 mt-4">
            <input
              type="checkbox"
              checked={newItem.is_active}
              onChange={(e) => setNewItem({ ...newItem, is_active: e.target.checked })}
            />
            Active
          </label>
          <button className="ml-auto mt-4 px-4 py-2 rounded-md bg-brand-gradient text-white text-sm font-semibold flex items-center gap-2">
            <FiPlus /> Add Category
          </button>
        </div>
      </form>

      <div className="space-y-6">
        {aiItems.map((item) => {
          const previewSrc = imageFiles[item.id] ? URL.createObjectURL(imageFiles[item.id]) : item.image;
          const selected = iconsToList(item.icons);
          return (
            <div key={item.id} className="bg-navy border border-navy-border rounded-xl p-5">
              <div className="grid md:grid-cols-4 gap-4 mb-4">
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Image</label>
                  {previewSrc && (
                    <img src={previewSrc} alt={item.title} className="w-full h-24 object-cover rounded-md border border-navy-border mb-1" />
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setImageFiles((prev) => ({ ...prev, [item.id]: e.target.files[0] }))}
                    className="text-xs text-gray-400 w-full"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Title</label>
                  <select
                    className={inputClass}
                    style={{ colorScheme: "dark" }}
                    value={item.title}
                    onChange={(e) => updateField(item.id, "title", e.target.value)}
                  >
                    <option value="" style={{ backgroundColor: "#13202D", color: "#ffffff" }}>Select a category...</option>
                    {titleOptions.map((title) => (
                      <option key={title} value={title} style={{ backgroundColor: "#13202D", color: "#ffffff" }}>{title}</option>
                    ))}
                  </select>
                  <label className="block text-xs text-gray-400 mb-1 mt-3">Subtitle</label>
                  <input
                    className={inputClass}
                    value={item.subtitle}
                    onChange={(e) => updateField(item.id, "subtitle", e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1">Order</label>
                  <input
                    type="number"
                    className={inputClass}
                    value={item.order}
                    onChange={(e) => updateField(item.id, "order", Number(e.target.value))}
                  />
                  <label className="flex items-center gap-2 text-sm text-gray-300 mt-3">
                    <input
                      type="checkbox"
                      checked={item.is_active}
                      onChange={(e) => updateField(item.id, "is_active", e.target.checked)}
                    />
                    Active
                  </label>
                </div>
                <div className="flex md:justify-end items-start">
                  <button
                    onClick={() => deleteItem(item.id)}
                    className="p-2 rounded-md text-red-400 hover:bg-red-500/10 flex items-center gap-1 text-xs"
                  >
                    <FiTrash2 size={14} /> Delete
                  </button>
                </div>
              </div>

              <label className="block text-xs text-gray-400 mb-1">Tools Used</label>
              <IconPicker selected={selected} onToggle={(key) => toggleIcon(item.id, key)} />

              <button
                onClick={() => saveItem(item)}
                className="mt-4 px-4 py-2 rounded-md bg-brand-gradient text-white text-xs font-semibold"
              >
                Save
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
